import { useState } from "react";
import { useSpring, useTransition, animated } from "@react-spring/web";
import { ExternalLink, GitBranch, GitCommit, GitPullRequest, Loader2, Unlink } from "lucide-react";
import {
  useGitConnection,
  useDisconnectGithub,
  useGitRepos,
  useGitSelectedRepo,
  useDefaultGitBranch,
} from "@/hooks/git";
import type { GitBranch as GitBranchType, GitConnection, GitRepo } from "@/services/git/types";
import type { Tab, SelectedItem } from "@/components/molecules/git/types";
import { GitLoadingView } from "@/components/molecules/git/GitLoadingView";
import { NotConnectedView } from "@/components/molecules/git/NotConnectedView";
import { RepoSelectorView } from "@/components/molecules/git/RepoSelectorView";
import { RepoPicker } from "@/components/molecules/git/RepoPicker";
import { BranchList } from "@/components/molecules/git/BranchList";
import { CommitList } from "@/components/molecules/git/CommitList";
import { PrList } from "@/components/molecules/git/PrList";
import { OverviewPane } from "@/components/molecules/git/OverviewPane";
import { BranchDetailPane } from "@/components/molecules/git/BranchDetailPane";
import { CommitDetailPane } from "@/components/molecules/git/CommitDetailPane";
import { PrDetailPane } from "@/components/molecules/git/PrDetailPane";
import { CreateBranchPane } from "@/components/molecules/git/CreateBranchPane";
import { CreatePrPane } from "@/components/molecules/git/CreatePrPane";
import { CreateRepoDropdown } from "@/components/organisms/git/CreateRepoDropdown";
import { DisconnectGitDialog } from "@/components/molecules/git/DisconnectGitDialog";
import { useMcpConnectors, useDeleteMcpConnector } from "@/hooks/tools";
import { isConnectorUsable } from "@/services/mcp-gateway";

// ─── Root Orchestrator ────────────────────────────────────────────────────────

export function GitLayout() {
  const { data: connection, isLoading: connectionLoading, error: connectionError } = useGitConnection();
  const { data: selectedRepo, isLoading: repoLoading } = useGitSelectedRepo({ enabled: !!connection });
  const { data: repos = [], isLoading: reposLoading } = useGitRepos({ enabled: !!connection });
  const { mutate: disconnect, isPending: disconnecting } = useDisconnectGithub();

  /*
   * Disconnecting Git leaves the GitHub MCP connector alone — two connections, one name.
   * People read the green GitHub dot Weave kept showing as a bug, so the choice is now
   * made out loud instead of in a tooltip. No connector, no question: disconnect straight
   * away rather than adding a modal that only ever says one thing.
   */
  const { data: mcpConnectors } = useMcpConnectors();
  const githubConnector = (mcpConnectors ?? []).find(
    (c) => c.provider === "github" && isConnectorUsable(c.status),
  );
  const { mutate: removeConnector, isPending: removingConnector } = useDeleteMcpConnector();
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  const askToDisconnect = () => {
    if (!githubConnector) { disconnect(); return; }
    setConfirmingDisconnect(true);
  };
  const disconnectGitOnly = () => { setConfirmingDisconnect(false); disconnect(); };
  const disconnectBoth = () => {
    setConfirmingDisconnect(false);
    disconnect();
    if (githubConnector) removeConnector(githubConnector.id);
  };

  const disconnectDialog = confirmingDisconnect && githubConnector ? (
    <DisconnectGitDialog
      githubConnector={githubConnector}
      busy={disconnecting || removingConnector}
      onDisconnectGitOnly={disconnectGitOnly}
      onDisconnectBoth={disconnectBoth}
      onCancel={() => setConfirmingDisconnect(false)}
    />
  ) : null;

  if (connectionLoading || (!!connection && repoLoading)) return <GitLoadingView />;
  if (!connection) return <NotConnectedView error={connectionError} />;

  if (!selectedRepo) {
    return (
      <>
        <RepoSelectorView
          repos={repos}
          reposLoading={reposLoading}
          onDisconnect={askToDisconnect}
          disconnecting={disconnecting}
        />
        {disconnectDialog}
      </>
    );
  }

  return (
    <>
      <ConnectedView
        connection={connection}
        selectedRepo={selectedRepo}
        repos={repos}
        reposLoading={reposLoading}
        onDisconnect={askToDisconnect}
        disconnecting={disconnecting}
      />
      {disconnectDialog}
    </>
  );
}

// ─── Connected — Split-pane Dashboard ─────────────────────────────────────────

function ConnectedView({
  connection, selectedRepo, repos, reposLoading, onDisconnect, disconnecting,
}: {
  connection: GitConnection;
  selectedRepo: GitRepo | undefined;
  repos: GitRepo[];
  reposLoading: boolean;
  onDisconnect: () => void;
  disconnecting: boolean;
}) {
  const [activeTab, setActiveTab] = useState<Tab>("commits");
  const [selectedItem, setSelectedItem] = useState<SelectedItem | null>(null);
  const { data: workingBranch } = useDefaultGitBranch();

  const TABS: Tab[] = ["branches", "commits", "prs"];
  const tabIdx = TABS.indexOf(activeTab);

  const underlineSpring = useSpring({
    left: `${((tabIdx + 0.5) / TABS.length) * 100}%`,
    config: { tension: 340, friction: 28 },
  });

  const listTransition = useTransition(activeTab, {
    from: { opacity: 0, transform: "translateY(4px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(0px)" },
    config: { tension: 260, friction: 32, clamp: true },
  });

  const detailTransition = useTransition(selectedItem, {
    from: { opacity: 0, transform: "translateY(5px)" },
    enter: { opacity: 1, transform: "translateY(0px)" },
    leave: { opacity: 0, transform: "translateY(0px)" },
    config: { tension: 260, friction: 32, clamp: true },
  });

  if (!selectedRepo) return <GitLoadingView />;

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-xl border border-border bg-card">

      {/* Header row */}
      <div className="flex shrink-0 items-stretch border-b border-border">
        {/* Left zone — aligns with list panel */}
        <div className="flex w-72 shrink-0 items-center border-r border-border px-3 py-2">
          <RepoPicker
            selectedRepo={selectedRepo}
            repos={repos}
            reposLoading={reposLoading}
            onSelect={() => setSelectedItem(null)}
          />
        </div>
        {/* Right zone — branch chip + actions */}
        <div className="flex flex-1 items-center gap-3 px-4 py-2">
          {workingBranch ? (
            <div className="flex items-center gap-2">
              <span className="text-[11px] text-muted-foreground">Selected branch</span>
              <span className="text-muted-foreground/40">·</span>
              <GitBranch className="h-3 w-3 shrink-0 text-emerald-400" />
              <span className="font-mono text-[11px] text-emerald-400">{workingBranch.name}</span>
            </div>
          ) : (
            <span className="text-[11px] text-muted-foreground/40">No branch selected</span>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            <CreateRepoDropdown />
            <a
              href={selectedRepo.html_url}
              target="_blank"
              rel="noreferrer"
              className="studio-button"
            >
              <ExternalLink className="h-4 w-4" />
              GitHub
            </a>
            <button
              type="button"
              onClick={onDisconnect}
              disabled={disconnecting}
              // Git-only: this clears the repo selection here. The GitHub OAuth
              // connection is shared with the Tools page and is disconnected there.
              title="Disconnects Git only — GitHub stays connected under Tools"
              className="studio-button text-muted-foreground hover:text-destructive"
            >
              {disconnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Unlink className="h-4 w-4" />}
              {disconnecting ? "Disconnecting…" : "Disconnect Git"}
            </button>
          </div>
        </div>
      </div>

      {/* Split pane — fills remaining space */}
      <div className="flex flex-1 min-h-0">
        {/* Left panel — list */}
        <div className="flex w-72 shrink-0 flex-col border-r border-border">
          {/* Tab strip — spring-animated underline */}
          <div className="relative flex border-b border-border">
            {TABS.map((tab) => {
              const icons: Record<Tab, React.ReactNode> = {
                branches: <GitBranch className="h-3.5 w-3.5" />,
                commits: <GitCommit className="h-3.5 w-3.5" />,
                prs: <GitPullRequest className="h-3.5 w-3.5" />,
              };
              const labels: Record<Tab, string> = { branches: "Branches", commits: "Commits", prs: "PRs" };
              return (
                <button
                  key={tab}
                  type="button"
                  onClick={() => { setActiveTab(tab); setSelectedItem(null); }}
                  className={[
                    "flex flex-1 items-center justify-center gap-1.5 py-2.5 text-xs transition-colors",
                    activeTab === tab ? "font-medium text-foreground" : "text-muted-foreground hover:text-foreground",
                  ].join(" ")}
                >
                  {icons[tab]}
                  {labels[tab]}
                </button>
              );
            })}
            <animated.div
              style={{ ...underlineSpring, transform: "translateX(-50%)" }}
              className="absolute bottom-0 h-[2px] w-14 rounded-full bg-primary"
            />
          </div>

          {/* List content — spring transition between tabs */}
          <div className="relative flex-1 overflow-hidden">
            {listTransition((style, tab) => (
              <animated.div style={style} className="absolute inset-0 overflow-y-auto">
                {tab === "branches" && (
                  <BranchList
                    repoFullName={selectedRepo.full_name}
                    defaultBranch={selectedRepo.default_branch}
                    selectedName={selectedItem?.type === "branch" ? selectedItem.name : null}
                    onSelect={(b: GitBranchType) => setSelectedItem({ type: "branch", name: b.name, sha: b.sha, id: b.id })}
                    onCreate={() => setSelectedItem({ type: "create-branch" })}
                  />
                )}
                {tab === "commits" && (
                  <CommitList
                    repoFullName={selectedRepo.full_name}
                    defaultBranch={workingBranch?.name}
                    selectedSha={selectedItem?.type === "commit" ? selectedItem.sha : null}
                    onSelect={(sha) => setSelectedItem({ type: "commit", sha })}
                  />
                )}
                {tab === "prs" && (
                  <PrList
                    repoFullName={selectedRepo.full_name}
                    selectedNumber={selectedItem?.type === "pr" ? selectedItem.number : null}
                    onSelect={(number) => setSelectedItem({ type: "pr", number })}
                    onCreate={() => setSelectedItem({ type: "create-pr" })}
                  />
                )}
              </animated.div>
            ))}
          </div>
        </div>

        {/* Right panel — detail with spring transition */}
        <div className="relative flex flex-1 min-w-0 overflow-hidden">
          {detailTransition((style, item) => (
            <animated.div
              style={{ ...style, zIndex: item === selectedItem ? 1 : 0 }}
              className="absolute inset-0 flex flex-col overflow-hidden"
            >
              {!item && (
                <OverviewPane connection={connection} selectedRepo={selectedRepo} />
              )}
              {item?.type === "branch" && (
                <BranchDetailPane
                  repo={selectedRepo}
                  branch={{ name: item.name, sha: item.sha, id: item.id }}
                  onClose={() => setSelectedItem(null)}
                  onCreatePr={() => { setSelectedItem({ type: "create-pr" }); setActiveTab("prs"); }}
                />
              )}
              {item?.type === "commit" && (
                <CommitDetailPane
                  repoFullName={selectedRepo.full_name}
                  sha={item.sha}
                  onClose={() => setSelectedItem(null)}
                />
              )}
              {item?.type === "pr" && (
                <PrDetailPane
                  repoFullName={selectedRepo.full_name}
                  number={item.number}
                  onClose={() => setSelectedItem(null)}
                />
              )}
              {item?.type === "create-branch" && (
                <CreateBranchPane
                  repoFullName={selectedRepo.full_name}
                  defaultBranch={selectedRepo.default_branch}
                  onSuccess={() => { setSelectedItem(null); setActiveTab("branches"); }}
                  onCancel={() => setSelectedItem(null)}
                />
              )}
              {item?.type === "create-pr" && (
                <CreatePrPane
                  repoFullName={selectedRepo.full_name}
                  defaultBranch={selectedRepo.default_branch}
                  onSuccess={(pr) => { setSelectedItem({ type: "pr", number: pr.number }); setActiveTab("prs"); }}
                  onCancel={() => setSelectedItem(null)}
                />
              )}
            </animated.div>
          ))}
        </div>
      </div>
    </div>
  );
}
